import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

const DEFAULT_NAVIGATION = [
    {
        name: 'Solutions',
        path: '/services',
        submenu: [
            { name: 'AI-Augmented Staffing', path: '/services/ai-staffing', icon: 'Zap' },
            { name: 'Cost Optimization', path: '/services/cost-optimization', icon: 'TrendingUp' },
            { name: 'Offshore Strategy', path: '/services/offshore-strategy', icon: 'Globe' },
            { name: 'Digital Transformation', path: '/services/digital-transformation', icon: 'Zap' }
        ]
    },
    {
        name: 'Industries',
        path: '/industries',
        submenu: [
            { name: 'Technology & IT', path: '/industries/technology', icon: 'Zap' },
            { name: 'Healthcare & Medical', path: '/industries/healthcare', icon: 'Award' },
            { name: 'E-commerce & Retail', path: '/industries/retail', icon: 'TrendingUp' },
            { name: 'Financial Services', path: '/industries/finance', icon: 'Award' },
            { name: 'Construction', path: '/industries/construction', icon: 'HardHat' },
            { name: 'Education', path: '/industries/education', icon: 'Book' },
            { name: 'Engineering', path: '/industries/engineering', icon: 'PenTool' },
            { name: 'Human Resources', path: '/industries/hr', icon: 'Users' },
            { name: 'Legal Services', path: '/industries/legal', icon: 'Scale' },
            { name: 'Logistics & Supply Chain', path: '/industries/logistics', icon: 'Truck' },
            { name: 'Manufacturing', path: '/industries/manufacturing', icon: 'Factory' },
            { name: 'Marketing & Advertising', path: '/industries/marketing', icon: 'Megaphone' },
            { name: 'Media & Entertainment', path: '/industries/media', icon: 'Film' },
            { name: 'Operations & Support', path: '/industries/operations', icon: 'Settings' },
            { name: 'Real Estate', path: '/industries/real-estate', icon: 'Home' },
            { name: 'Sales & Biz Dev', path: '/industries/sales', icon: 'Briefcase' },
            { name: 'Travel & Hospitality', path: '/industries/travel', icon: 'Plane' }
        ]
    },
    {
        name: 'Resources',
        path: '/resources',
        submenu: [
            { name: 'BPO Glossary', path: '/glossary', icon: 'BookOpen' },
            { name: 'Case Studies', path: '/case-studies', icon: 'Award' },
            { name: 'ROI Calculator', path: '/calculator', icon: 'Calculator' },
            { name: 'Blog & Insights', path: '/blog', icon: 'BookOpen' }
        ]
    },
    {
        name: 'Training',
        path: '/training',
        submenu: [
            { name: 'AI-Augmented BD Program', path: '/training/ai-bd', icon: 'Zap' },
            { name: 'Experienced BD Track', path: '/training/experienced-bd', icon: 'Award' },
            { name: 'Aspiring BD Track', path: '/training/aspiring-bd', icon: 'Users' },
            { name: 'Webinars & Events', path: '/training/webinars', icon: 'Users' }
        ]
    },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' }
];

const Navigation = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const [navigation, setNavigation] = useState(DEFAULT_NAVIGATION);
    const location = useLocation();

    useEffect(() => {
        const fetchNavigation = async () => {
            try {
                const docRef = doc(db, 'settings', 'navigation');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists() && docSnap.data().items) {
                    setNavigation(docSnap.data().items);
                }
            } catch (error) {
                console.error("Error fetching navigation:", error);
            }
        };

        fetchNavigation();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menu when route changes
    useEffect(() => {
        setIsMenuOpen(false);
        setActiveDropdown(null);
    }, [location]);

    const isActive = (path) => location.pathname.startsWith(path);

    return (
        <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#20222D] shadow-lg' : 'bg-[#20222D]'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-20">
                    {/* Logo */}
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 cursor-pointer group">
                        <img
                            src="/assets/images/logo-transparent.png"
                            alt="BPO Consultants Logo"
                            className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="flex flex-col">
                            <span className="text-xl font-bold text-white leading-none tracking-tight">
                                BPO <span className="text-[#A2CB52]">Consultants</span>
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-1">
                        {navigation.map((item) => (
                            <div
                                key={item.name}
                                className="relative"
                                onMouseEnter={() => item.submenu && setActiveDropdown(item.name)}
                                onMouseLeave={() => setActiveDropdown(null)}
                            >
                                <Link
                                    to={item.path}
                                    className={`px-4 py-2 text-base font-medium transition-colors duration-200 rounded-xl flex items-center ${isActive(item.path)
                                        ? 'text-[#A2CB52] bg-[#A2CB52]/10'
                                        : 'text-white hover:text-[#A2CB52] hover:bg-white/5'
                                        }`}
                                >
                                    {item.name}
                                    {item.submenu && <ChevronDown size={16} className="ml-1" />}
                                </Link>

                                {/* Dropdown Menu */}
                                {item.submenu && activeDropdown === item.name && (
                                    <div className={`absolute top-full left-0 z-50 pt-2 ${item.name === 'Industries' ? 'w-[800px] -left-20' : 'w-64'}`}>
                                        <div className={`bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 ${item.name === 'Industries' ? 'grid grid-cols-3 gap-1 p-3' : ''}`}>
                                            {item.submenu.map((subitem, index) => (
                                                <Link
                                                    key={index}
                                                    to={subitem.path}
                                                    className={`w-full text-left px-4 py-3 hover:bg-[#ECF5DC] transition-colors flex items-start group rounded-xl ${item.name === 'Industries' ? 'py-2 px-3' : ''}`}
                                                >
                                                    <div className="flex items-start w-full">
                                                        <div className="flex-1">
                                                            <div className="font-bold text-[#20222D] group-hover:text-[#A2CB52] transition-colors text-sm">
                                                                {subitem.name}
                                                            </div>
                                                        </div>
                                                        {item.name !== 'Industries' && <ArrowRight className="text-[#A2CB52] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2" size={16} />}
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <Link
                            to="/contact"
                            className="bg-[#A2CB52] text-[#20222D] px-6 py-2.5 rounded-2xl font-bold hover:bg-[#B5D575] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ml-2"
                        >
                            Free Consultation
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden text-white p-2"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                    >
                        {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div className="md:hidden bg-[#20222D] border-t border-[#A2CB52]/20">
                    <div className="px-4 pt-2 pb-4 space-y-1 max-h-[70vh] overflow-y-auto">
                        {navigation.map((item) => (
                            <div key={item.name}>
                                {item.submenu ? (
                                    <div>
                                        <button
                                            onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                                            className="w-full text-left px-4 py-3 rounded-xl text-white hover:bg-[#A2CB52]/10 transition-colors flex items-center justify-between"
                                        >
                                            <span className="font-medium">{item.name}</span>
                                            <ChevronDown
                                                size={20}
                                                className={`transition-transform ${activeDropdown === item.name ? 'rotate-180' : ''}`}
                                            />
                                        </button>
                                        {activeDropdown === item.name && (
                                            <div className="ml-4 mt-1 space-y-1">
                                                {item.submenu.map((subitem, index) => (
                                                    <Link
                                                        key={index}
                                                        to={subitem.path}
                                                        className="block w-full text-left px-4 py-2 rounded-lg text-gray-300 hover:text-[#A2CB52] hover:bg-white/5 transition-colors text-sm"
                                                    >
                                                        {subitem.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Link
                                        to={item.path}
                                        className={`block w-full text-left px-4 py-3 rounded-xl transition-colors ${isActive(item.path)
                                            ? 'bg-[#A2CB52] text-[#20222D] font-bold'
                                            : 'text-white hover:bg-[#A2CB52]/10'
                                            }`}
                                    >
                                        {item.name}
                                    </Link>
                                )}
                            </div>
                        ))}
                        <Link
                            to="/contact"
                            className="block w-full text-center bg-[#A2CB52] text-[#20222D] px-6 py-3 rounded-2xl font-bold hover:bg-[#B5D575] transition-all mt-4"
                        >
                            Free Consultation
                        </Link>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navigation;
